import { useEffect } from "react";
import { useBlocker } from "react-router-dom";
// @ts-ignore - This is a workaround to fix the type error
import ReactRouterPrompt from "react-router-prompt";

import { ConfirmDialog } from "./ConfirmDialog";

type PromptDialogProps = {
  header?: string;
  info?: string;
  confirmText?: string;
  minWidth?: string;
  maxWidth?: string;
};

type UnfinishedChangesPrompt = PromptDialogProps & {
  when: boolean;
  /**
   * Also ask before a refresh, tab close, or any other browser-level unload.
   * Turn off when nothing is lost on unload: a same-tab file download fires
   * `beforeunload` too, so the browser's "changes may not be saved" warning
   * would appear on every download.
   */
  warnOnUnload?: boolean;
};

export function UnfinishedChangesPrompt({
  when,
  warnOnUnload = true,
  ...dialogProps
}: UnfinishedChangesPrompt) {
  if (!warnOnUnload) {
    return <InAppNavigationPrompt when={when} {...dialogProps} />;
  }

  return (
    <ReactRouterPrompt when={when}>
      {({
        isActive,
        onConfirm,
        onCancel,
      }: {
        isActive: boolean;
        onConfirm: () => void;
        onCancel: () => void;
      }) => (
        <PromptDialog open={isActive} onConfirm={onConfirm} onCancel={onCancel} {...dialogProps} />
      )}
    </ReactRouterPrompt>
  );
}

function InAppNavigationPrompt({ when, ...dialogProps }: PromptDialogProps & { when: boolean }) {
  const blocker = useBlocker(when);

  useEffect(() => {
    if (blocker.state === "blocked" && !when) {
      blocker.reset();
    }
  }, [blocker, when]);

  return (
    <PromptDialog
      open={blocker.state === "blocked"}
      onConfirm={() => {
        if (blocker.state === "blocked") blocker.proceed();
      }}
      onCancel={() => {
        if (blocker.state === "blocked") blocker.reset();
      }}
      {...dialogProps}
    />
  );
}

function PromptDialog({
  open,
  onConfirm,
  onCancel,
  header,
  info,
  confirmText,
  minWidth,
  maxWidth,
}: PromptDialogProps & { open: boolean; onConfirm: () => void; onCancel: () => void }) {
  return (
    <ConfirmDialog
      open={open}
      onConfirm={onConfirm}
      onClose={onCancel}
      minWidth={minWidth}
      maxWidth={maxWidth}
      header={header ?? "You have unsaved changes. Are you sure you want to leave?"}
      info={info}
      confirmText={confirmText ?? "Yes, I'm Sure"}
    />
  );
}
